/**
 * Guided-tour context object, types and hook (EPIC #1321, part 1/9).
 *
 * Split out of TourContext.tsx so the .tsx module exports ONLY the provider
 * component (keeps React Fast Refresh + the
 * ``react-refresh/only-export-components`` lint rule clean — the same pattern
 * the auth/branding/modules contexts use).
 *
 * The default value is a safe no-op so components that read ``useTour`` (the
 * sidebar + layout, which the engine drives for nav orchestration) never throw
 * when rendered outside a TourProvider — e.g. in isolated unit tests.
 */
import { createContext, useContext } from "react";
import type { TourStep } from "../lib/tours/types";

/**
 * A one-off hint (D10-ACT-004, #3606): the help assistant pointing at one
 * control with its own text. Runs through the tour engine as a single synthetic
 * step (so it looks exactly like a tour tooltip), but is not a registered tour
 * and never records tour progress.
 */
export interface TourHint {
  /** Static route the control lives on; the engine opens it first. */
  route: string;
  /** The control's ``data-tour`` value. */
  target: string;
  /** Tooltip heading (the assistant's name). */
  title: string;
  /** Tooltip text, rendered as plain text. */
  body: string;
}

/** ``shown``; ``missing`` (the control is not on the page); ``busy`` (a tour is running). */
export type HintResult = "shown" | "missing" | "busy";

export interface ShowHintOptions {
  /** Called once when the hint is closed (not called if it never showed). */
  onEnd?: () => void;
  /** How long to look for the control after opening the page (ms). */
  waitMs?: number;
}

/** Reserved id prefix for synthetic hint "tours" -- never a registered tour id. */
export const HINT_ID_PREFIX = "support-hint:";

export function isHintTourId(id: string | null | undefined): boolean {
  return typeof id === "string" && id.startsWith(HINT_ID_PREFIX);
}

export interface StartTourOptions {
  /** Called once when the tour ends (finished, skipped or cancelled). */
  onEnd?: () => void;
}

export interface TourContextValue {
  /** Id of the running tour, or null when idle. */
  activeTourId: string | null;
  /** True while a tour is running. */
  isRunning: boolean;
  /** The step the engine is currently on (already branded), or null. */
  currentStep: TourStep | null;
  /** Current 0-based step index. */
  stepIndex: number;
  /** True while a one-off hint (``showHint``) rather than a real tour is showing. */
  isHint: boolean;
  /** Total number of VISIBLE steps in the active tour for this user. */
  stepCount: number;
  /** True when there is a later section to skip to (REQ D4-ONB-003). */
  hasNextSection: boolean;

  /**
   * Start a tour by id. No-op for an unknown tour or one with no visible steps.
   * ``options.onEnd`` runs once when THIS tour ends, however it ends (the help
   * assistant uses it to bring its panel back, #3624); a later tour or hint
   * replaces it.
   */
  startTour: (tourId: string, options?: StartTourOptions) => void;
  /** Advance one step (finishes the tour on the last step). */
  next: () => void;
  /** Go back one step (no-op on the first step). */
  back: () => void;
  /** Jump to the first step of the next section (no-op if none). */
  skipSection: () => void;
  /** End the tour. ``outcome`` defaults to "cancelled" (the X / Esc path). */
  endTour: (outcome?: "skipped" | "cancelled") => void;
  /**
   * Point at one control with a tooltip (D10-ACT-004). Opens ``hint.route`` if
   * needed, then looks for the anchor for a short while (not the tours' 8s
   * wait); a missing or hidden anchor is a no-op (``missing``). Refused while a
   * tour runs (``busy``). Never records ``TourProgress``.
   */
  showHint: (hint: TourHint, options?: ShowHintOptions) => Promise<HintResult>;

  /** Orchestration: sidebar accordion group the engine wants expanded. */
  navGroupToExpand: string | null;
  /** Orchestration: whether the engine wants the mobile drawer open (< lg). */
  mobileDrawerOpen: boolean;
}

const NOOP_TOUR: TourContextValue = {
  activeTourId: null,
  isRunning: false,
  currentStep: null,
  isHint: false,
  stepIndex: 0,
  stepCount: 0,
  hasNextSection: false,
  startTour: () => {},
  next: () => {},
  back: () => {},
  skipSection: () => {},
  endTour: () => {},
  showHint: async () => "busy",
  navGroupToExpand: null,
  mobileDrawerOpen: false,
};

export const TourContext = createContext<TourContextValue>(NOOP_TOUR);

export function useTour(): TourContextValue {
  return useContext(TourContext);
}
